/**
 * Hierarchical, type-safe query-key factory.
 *
 * Single-entity keys are prefixed by their list base (e.g. `sunday` starts
 * with the same segment as `sundays`) so a single `invalidateQueries` call on
 * the base prefix can match every related query. Keys are returned `as const`
 * so TypeScript narrows them to literal tuples rather than `string[]`.
 *
 * `podcastEpisode` is suffixed with `'detail'` so a single-episode key never
 * collides with the episode-list key for the same podcast slug.
 */
export const queryKeys = {
  sundays: ['sundays'] as const,
  sundaysList: (sortDirection: string) => ['sundays', 'list', sortDirection] as const,
  sundaysIndex: ['sundays', 'index'] as const,
  sunday: (dateString: string) => ['sundays', dateString] as const,
  sermons: ['sermons'] as const,
  sermon: (dateString: string) => ['sermons', dateString] as const,
  podcasts: ['podcastEpisodes'] as const,
  podcastEpisodes: (slug: string) => ['podcastEpisodes', slug] as const,
  podcastEpisode: (slug: string) => ['podcastEpisodes', slug, 'detail'] as const,
  managedUsers: ['managedUsers'] as const,
  servicePlans: ['servicePlans'] as const,
  servicePlansList: ['servicePlans', 'list'] as const,
  servicePlanDates: (year: number) => ['servicePlans', 'dates', year] as const,
  bulletins: ['bulletins'] as const,
  bulletin: (dateString: string) => ['bulletins', dateString] as const,
  bulletinDates: ['bulletins', 'dates'] as const,
  songs: ['songs'] as const,
  songsList: ['songs', 'list'] as const,
  songLyrics: ['songLyrics'] as const,
  songLyricsFor: (songId: string) => ['songLyrics', songId] as const,
  songLyricsMany: (songIds: readonly string[]) => ['songLyrics', 'many', songIds] as const,
  songUsages: ['songUsages'] as const,
  songUsagesList: ['songUsages', 'list'] as const,
  slideConfig: ['settings', 'slides'] as const,
  equippingCategories: ['settings', 'equipping'] as const,
  people: ['people'] as const,
  peopleList: ['people', 'list'] as const,
  masterPlan: ['masterPlan'] as const,
  masterPlanYear: (year: number) => ['masterPlan', 'year', year] as const,
  handouts: ['handouts'] as const,
  planHandouts: (dateString: string) => ['handouts', dateString] as const,
  publishedHandouts: (dateString: string) => ['bulletins', dateString, 'handouts'] as const,
  publishedHandout: (dateString: string, handoutId: string) =>
    ['bulletins', dateString, 'handouts', handoutId] as const,
};
